import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, QueryErrorAlert } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { isApiError } from '@/services/api';
import { fetchPlanServices } from '../services/plans';
import { usePlanServiceMutations, planServicesQueryKey } from '../hooks/usePlanServiceMutations';
import { PlanServiceDataTable } from './PlanServiceDataTable';
import { ServiceForm } from './ServiceForm/ServiceForm';
import { ConfirmActionModal } from '@shared/components';
import type { BackRef } from '../types/backRef';
import type { PlanServiceRow } from '../types/plan.types';

interface Props {
    planId: string;
    planName: string;
}

export const PlanServicePanel: React.FC<Props> = ({ planId, planName }) => {
    // If this plan was reached from a product (its Plans tab), the product
    // origin arrives as {backTo, backLabel} in router state. Forward it so a
    // service reached from here can return through the plan to that product.
    const { state } = useLocation();
    const planOrigin = (state as { backTo?: string; backLabel?: string } | null) ?? null;

    const serviceBackRef: BackRef = {
        path: `/smr-admin/plans/${planId}?tab=services`,
        label: planName,
        ...(planOrigin?.backTo
            ? { state: { backTo: planOrigin.backTo, backLabel: planOrigin.backLabel } }
            : {}),
    };

    const [showForm, setShowForm] = useState(false);
    const [pendingRemove, setPendingRemove] = useState<PlanServiceRow | null>(null);
    const [removeError, setRemoveError] = useState<string | null>(null);
    const [restoreFocusAfterRemove, setRestoreFocusAfterRemove] = useState(false);

    const panelRef = useRef<HTMLDivElement>(null);
    const addServiceRef = useRef<HTMLDivElement>(null);
    const queryErrorRef = useRef<HTMLDivElement>(null);

    const { data: services = [], isLoading, isError, isFetching, refetch } = useQuery({
        queryKey: planServicesQueryKey(planId),
        queryFn: () => fetchPlanServices(planId),
        enabled: Boolean(planId),
    });

    const { createMutation, updateMutation, removeMutation } = usePlanServiceMutations(planId);

    // The removed row takes its Remove trigger with it, so the dialog's focus restore
    // would land on document.body. Retry outranks Add, which is hidden once the refetch fails.
    useEffect(() => {
        if (!restoreFocusAfterRemove) return;
        const target = queryErrorRef.current?.querySelector('button')
            ?? addServiceRef.current?.querySelector('button')
            ?? panelRef.current;
        target?.focus();
        setRestoreFocusAfterRemove(false);
    }, [restoreFocusAfterRemove]);

    const confirmRemove = () => {
        if (!pendingRemove) return;
        setRemoveError(null);
        removeMutation.mutate(pendingRemove.id, {
            onSuccess: () => {
                setPendingRemove(null);
                setRestoreFocusAfterRemove(true);
            },
            onError: (err) => {
                setPendingRemove(null);
                setRemoveError(isApiError(err) ? err.message : 'Could not remove the service. Try again.');
            },
        });
    };

    return (
        <div ref={panelRef} tabIndex={-1} className="space-y-6 focus:outline-none">
            {removeError && <Alert variant="error">{removeError}</Alert>}

            {isLoading ? (
                <PlanServiceDataTable
                    planId={planId}
                    data={[]}
                    isLoading
                    onUpdate={() => Promise.resolve()}
                    onRemove={() => {}}
                />
            ) : isError ? (
                <div ref={queryErrorRef}>
                    <QueryErrorAlert
                        message="Could not load this plan's services. The schedule is unknown until it loads."
                        onRetry={() => { void refetch(); }}
                        isRetrying={isFetching}
                    />
                </div>
            ) : services.length === 0 ? (
                <p className="text-mp-muted text-sm">No services attached.</p>
            ) : (
                <PlanServiceDataTable
                    planId={planId}
                    data={services}
                    backRef={serviceBackRef}
                    onUpdate={(serviceId, values) =>
                        updateMutation.mutateAsync({ serviceId, payload: values })}
                    onRemove={setPendingRemove}
                    isUpdating={updateMutation.isPending}
                    removingServiceId={removeMutation.isPending ? (removeMutation.variables ?? null) : null}
                />
            )}

            <PermissionGuard permission={{ resource: 'smr', action: 'plan.edit' }}>
                <div ref={addServiceRef} className="border-t border-mp-border pt-6 max-w-2xl" hidden={isError}>
                    {showForm ? (
                        <ServiceForm
                            planId={planId}
                            idPrefix="plan-service-add"
                            submitLabel="Add Service"
                            pendingLabel="Adding..."
                            isLoading={createMutation.isPending}
                            genericErrorMessage="Could not create the service. Try again."
                            hideStatus
                            onSubmit={async (values) => {
                                await createMutation.mutateAsync(values);
                                setShowForm(false);
                            }}
                            onCancel={() => setShowForm(false)}
                        />
                    ) : (
                        <div>
                            <h3 className="font-semibold mb-2">Add Service</h3>
                            <div className="flex items-start gap-4 justify-between">
                                <p className="text-sm text-mp-muted">
                                    Create a new service for this plan with its position and set points
                                    (months and miles from first registration).
                                </p>
                                <Button
                                    size="sm"
                                    type="button"
                                    variant="primary"
                                    onClick={() => setShowForm(true)}
                                    className="whitespace-nowrap"
                                >
                                    Add Service
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </PermissionGuard>

            <ConfirmActionModal
                isOpen={pendingRemove !== null}
                title="Remove service from schedule?"
                message={pendingRemove && (
                    <>
                        <strong>{pendingRemove.name}</strong> (position {pendingRemove.position}) is deleted from{' '}
                        <strong>{planName}</strong>. Services after it move up one position to close the gap.
                    </>
                )}
                confirmLabel="Remove Service"
                confirmVariant="danger"
                isPending={removeMutation.isPending}
                onConfirm={confirmRemove}
                onClose={() => setPendingRemove(null)}
            />
        </div>
    );
};
